import { z } from 'zod';
// ============================================
// Auth Validation
// ============================================
/**
 * Base password rules — shared by register, reset-password (API) and the
 * register/reset forms (web) so the policy can never drift between them.
 */
export const passwordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128)
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number');
export const registerSchema = z.object({
    email: z.string().email('Please enter a valid email address').max(255),
    password: passwordSchema,
    name: z.string().min(2, 'Name must be at least 2 characters').max(100),
});
export const loginSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(1, 'Password is required'),
});
/** Request a password-reset email. */
export const resetPasswordSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
});
/** Submit a new password with a reset token (API endpoint body). */
export const resetPasswordSubmitSchema = z.object({
    token: z.string().min(1),
    password: passwordSchema,
});
/** New password form with confirmation (web reset-password page). */
export const newPasswordSchema = z
    .object({
    password: passwordSchema,
    confirmPassword: z.string(),
})
    .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
});
// ============================================
// Claim Validation
// ============================================
export const damageTypes = [
    'tyre',
    'wheel',
    'suspension',
    'bodywork',
    'steering',
    'other',
];
export const documentTypes = [
    'pothole_photo',
    'damage_photo',
    'repair_invoice',
    'repair_quote',
    'dashcam',
    'witness_statement',
    'other',
];
export const claimStatuses = [
    'draft',
    'evidence_complete',
    'pack_generated',
    'submitted',
    'awaiting_response',
    'response_received',
    'escalated',
    'resolved',
];
export const customerPrefixes = ['Mr', 'Mrs', 'Miss', 'Mx'];
export const ukPostcodeSchema = z
    .string()
    .regex(/^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i, 'Enter a valid UK postcode');
// NOTE: postcode fields here are deliberately length-limited rather than
// regex-validated — geocoded values pass straight through the API, and the
// web forms apply `ukPostcodeSchema` to user-typed input at the UI layer.
export const createClaimSchema = z.object({
    // Customer details (claimant)
    customerPrefix: z.enum(customerPrefixes).optional(),
    customerForename: z.string().max(100).optional(),
    customerSurname: z.string().max(100).optional(),
    customerAddressLine1: z.string().max(255).optional(),
    customerAddressLine2: z.string().max(255).optional(),
    customerCity: z.string().max(100).optional(),
    customerPostcode: z.string().max(10).optional(),
    customerPlaceId: z.string().max(255).optional(),
    // Incident details
    incidentDate: z.string().refine((v) => !Number.isNaN(Date.parse(v)), {
        message: 'incidentDate must be a valid date string',
    }),
    incidentTime: z.string().max(10).optional(),
    // Location
    locationAddress: z.string().max(500).optional(),
    locationPostcode: z.string().max(10).optional(),
    locationLat: z.number().min(-90).max(90).optional(),
    locationLng: z.number().min(-180).max(180).optional(),
    locationWhat3Words: z.string().max(100).optional(),
    // Damage details
    damageType: z.array(z.enum(damageTypes)).optional(),
    damageDescription: z.string().max(5000).optional(),
    repairCost: z.number().min(0).optional(),
    // Vehicle
    vehicleReg: z.string().max(10).optional(),
    vehicleMake: z.string().max(100).optional(),
    vehicleModel: z.string().max(100).optional(),
    vehicleYear: z.number().optional(),
    vehicleColour: z.string().max(50).optional(),
    vehicleFuelType: z.string().max(50).optional(),
    vehicleEngineCC: z.number().optional(),
    vehicleLookupData: z.record(z.unknown()).optional(),
});
// NOTE: claim status is deliberately NOT client-updatable — transitions happen
// via dedicated endpoints (outcome, pack generation, evidence analysis).
export const updateClaimSchema = createClaimSchema.partial().extend({
    authorityId: z.string().uuid().optional(),
    authorityAddress: z.string().max(500).optional(), // User-provided council postal address
});
// ============================================
// Payment Validation
// ============================================
export const productTypes = ['council_pack', 'court_pack'];
export const createCheckoutSchema = z.object({
    claimId: z.string().uuid(),
    productType: z.enum(productTypes),
});
// ============================================
// Prior Reports Validation
// ============================================
export const linkPriorReportSchema = z.object({
    externalReportId: z.string().min(1).max(100),
    source: z.enum(['fixmystreet']).default('fixmystreet'),
    userConfirmedMatch: z.boolean().default(false),
});
export const searchPriorReportsSchema = z.object({
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    radius: z.number().min(10).max(500).default(50),
});
// ============================================
// Document Validation
// ============================================
export const uploadDocumentSchema = z.object({
    type: z.enum(documentTypes),
});
// ============================================
// User Profile Validation
// ============================================
export const updateProfileSchema = z.object({
    name: z.string().min(2).max(100).optional(),
    phone: z.string().max(20).optional(),
    addressLine1: z.string().max(255).optional(),
    addressLine2: z.string().max(255).optional(),
    city: z.string().max(100).optional(),
    postcode: ukPostcodeSchema.optional(),
});
